import React from 'react';
import {
    Show,
    SimpleShowLayout,
    TextField,
    DateField,
    BooleanField,
    FunctionField,
    useTranslate,
    useRecordContext,
} from 'react-admin';
import {
    Box,
    Typography,
    Chip,
    Card,
    CardContent,
    CardMedia,
    IconButton,
} from '@mui/material';
import {
    Message,
    Settings,
    People,
    Schedule,
    Notifications,
    ArrowBack,
    Info,
    CheckCircle,
    AccessTime,
    BarChart,
    Campaign,
    Warning,
    Person,
} from '@mui/icons-material';

// Type icon component
const TypeIcon = ({ type }: { type: string }) => {
    switch (type) {
        case 'announcement':
            return <Campaign fontSize="small" />;
        case 'promotion':
            return <Info fontSize="small" />;
        case 'system':
            return <Notifications fontSize="small" />;
        case 'personal':
            return <Person fontSize="small" />;
        case 'alert':
            return <Warning fontSize="small" />;
        default:
            return <Info fontSize="small" />;
    }
};

const PageHeader = () => {
    const record = useRecordContext();
    const translate = useTranslate();

    if (!record) return null;

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 4,
        }}>
            <IconButton
                onClick={() => window.history.back()}
                size="large"
                sx={{
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title="Back to Messages"
            >
                <ArrowBack />
            </IconButton>
            <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' }, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {record.title}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FunctionField
                        render={(record: any) => {
                            return (
                                <Chip
                                    icon={<TypeIcon type={record.type} />}
                                    label={translate(`resources.messages.types.${record.type || 'announcement'}`)}
                                    size="small"
                                    variant="outlined"
                                />
                            );
                        }}
                    />
                    <FunctionField
                        render={(record: any) => {
                            const wasSent = record?.total_recipients > 0;
                            let color: 'default' | 'primary' | 'success' | 'error' = 'default';
                            let icon;
                            let statusKey = 'draft';

                            if (wasSent) {
                                color = 'success';
                                icon = <CheckCircle fontSize="small" />;
                                statusKey = 'sent';
                            } else {
                                color = 'default';
                                statusKey = 'draft';
                            }

                            return (
                                <Chip
                                    label={translate(`resources.messages.statuses.${statusKey}`)}
                                    color={color}
                                    size="small"
                                    icon={icon}
                                    sx={{ fontWeight: 600 }}
                                />
                            );
                        }}
                    />
                    <FunctionField
                        render={(record: any) => {
                            let color: 'default' | 'primary' | 'warning' | 'error' = 'primary';

                            switch (record.priority) {
                                case 'low':
                                    color = 'default';
                                    break;
                                case 'normal':
                                    color = 'primary';
                                    break;
                                case 'high':
                                    color = 'warning';
                                    break;
                                case 'urgent':
                                    color = 'error';
                                    break;
                            }

                            return (
                                <Chip
                                    label={translate(`resources.messages.priorities.${record.priority || 'normal'}`)}
                                    color={color}
                                    size="small"
                                    variant="outlined"
                                />
                            );
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
};

const MessageStatistics = () => {
    const record = useRecordContext();
    const translate = useTranslate();

    if (!record || !record.total_recipients || record.total_recipients === 0) return null;

    return (
        <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <BarChart sx={{ mr: 2, color: '#00C853' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {translate('resources.messages.sections.statistics')}
                    </Typography>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 3 }}>
                    <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                        <Typography variant="h3" color="primary" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '3rem' } }}>
                            {record.total_recipients || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {translate('resources.messages.fields.totalRecipients')}
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                        <Typography variant="h3" color="success.main" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '3rem' } }}>
                            {record.total_reads || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {translate('resources.messages.fields.totalReads')}
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                        <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '3rem' } }}>
                            {record.total_recipients > 0
                                ? `${Math.round((record.total_reads / record.total_recipients) * 100)}%`
                                : '0%'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {translate('resources.messages.fields.readRate')}
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                        <Typography variant="h3" color="warning.main" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '3rem' } }}>
                            {record.total_clicks || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {translate('resources.messages.fields.totalClicks')}
                        </Typography>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

const MessageShow = () => {
    const translate = useTranslate();

    return (
        <Show
            actions={false}
            title={false}
            component="div"
            sx={{
                backgroundColor: 'transparent',
                '& .RaShow-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleShowLayout
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                }}
            >
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 4, sm: 2 } }}>
                    <PageHeader />
                    <MessageStatistics />

                    {/* Message Content Section */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Message sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.messages.sections.content')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    source="title"
                                    label={translate('resources.messages.fields.title')}
                                />

                                <FunctionField
                                    render={(record: any) => {
                                        if (record.subtitle) {
                                            return (
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                                        Subtitle
                                                    </Typography>
                                                    <Typography variant="body1">{record.subtitle}</Typography>
                                                </Box>
                                            );
                                        }
                                        return null;
                                    }}
                                />

                                <TextField
                                    source="content"
                                    label={translate('resources.messages.fields.content')}
                                />

                                <FunctionField
                                    render={(record: any) => {
                                        if (record.image_url) {
                                            return (
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                                        {translate('resources.messages.fields.image')}
                                                    </Typography>
                                                    <Card sx={{ position: 'relative', mt: 1 }}>
                                                        <CardMedia
                                                            component="img"
                                                            height="200"
                                                            image={record.image_url}
                                                            alt="Message banner"
                                                            sx={{ objectFit: 'cover' }}
                                                        />
                                                        <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {translate('resources.messages.imagePreview')}
                                                            </Typography>
                                                        </Box>
                                                    </Card>
                                                </Box>
                                            );
                                        }
                                        return null;
                                    }}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Message Properties */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Settings sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.messages.sections.properties')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 2 }}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {translate('resources.messages.fields.type')}
                                    </Typography>
                                    <FunctionField
                                        render={(record: any) => (
                                            <Typography variant="body1">
                                                {translate(`resources.messages.types.${record.type || 'announcement'}`)}
                                            </Typography>
                                        )}
                                    />
                                </Box>

                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {translate('resources.messages.fields.priority')}
                                    </Typography>
                                    <FunctionField
                                        render={(record: any) => (
                                            <Typography variant="body1">
                                                {translate(`resources.messages.priorities.${record.priority || 'normal'}`)}
                                            </Typography>
                                        )}
                                    />
                                </Box>

                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {translate('resources.messages.fields.validFrom')}
                                    </Typography>
                                    <DateField source="valid_from" showTime />
                                </Box>

                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {translate('resources.messages.fields.validUntil')}
                                    </Typography>
                                    <FunctionField
                                        render={(record: any) => (
                                            <Typography variant="body1">
                                                {record.valid_until ?
                                                    new Date(record.valid_until).toLocaleString() :
                                                    translate('resources.messages.noExpiry')
                                                }
                                            </Typography>
                                        )}
                                    />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Target Audience */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <People sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.messages.sections.targeting')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {translate('resources.messages.fields.targetType')}
                                    </Typography>
                                    <FunctionField
                                        render={(record: any) => (
                                            <Typography variant="body1">
                                                {translate(`resources.messages.targetTypes.${record.target_type || 'all'}`)}
                                            </Typography>
                                        )}
                                    />
                                </Box>

                                <FunctionField
                                    render={(record: any) => {
                                        if (record.target_membership_levels?.length > 0) {
                                            return (
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                                        {translate('resources.messages.fields.membershipLevels')}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                        {record.target_membership_levels.map((level: string) => (
                                                            <Chip
                                                                key={level}
                                                                label={translate(`resources.messages.memberships.${level}`)}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        ))}
                                                    </Box>
                                                </Box>
                                            );
                                        }

                                        if (record.target_user_ids?.length > 0) {
                                            return (
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                                        {translate('resources.messages.fields.targetUsers')}
                                                    </Typography>
                                                    <Typography variant="body1">{record.target_user_ids.length} users selected</Typography>
                                                </Box>
                                            );
                                        }
                                        return null;
                                    }}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Delivery & Actions */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Notifications sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.messages.sections.delivery')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {/* Push Notification Setting */}
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {translate('resources.messages.fields.sendPush')}
                                    </Typography>
                                    <BooleanField source="send_push" />
                                </Box>

                                {/* Push Notification Details */}
                                <FunctionField
                                    render={(record: any) => {
                                        if (record.send_push) {
                                            return (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                                        {translate('resources.messages.fields.pushNotificationDetails')}
                                                    </Typography>
                                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 2fr' }, gap: 2 }}>
                                                        <Box>
                                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                                {translate('resources.messages.fields.pushTitle')}
                                                            </Typography>
                                                            <Typography variant="body1">{record.push_title || record.title}</Typography>
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                                {translate('resources.messages.fields.pushBody')}
                                                            </Typography>
                                                            <Typography variant="body1" sx={{
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 3,
                                                                WebkitBoxOrient: 'vertical'
                                                            }}>
                                                                {record.push_body || record.content}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            );
                                        }
                                        return null;
                                    }}
                                />
                            </Box>

                            {/* Action Configuration */}
                            <FunctionField
                                render={(record: any) => {
                                    if (record.action_type && record.action_type !== 'none') {
                                        return (
                                            <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e0e0e0' }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                                                    {translate('resources.messages.sections.action')}
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: { xs: 2, sm: 3 }, flexDirection: { xs: 'column', sm: 'row' } }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                                            {translate('resources.messages.fields.actionType')}
                                                        </Typography>
                                                        <Typography variant="body1">
                                                            {translate(`resources.messages.actionTypes.${record.action_type}`)}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 2 }}>
                                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                                            {translate('resources.messages.fields.actionTarget')}
                                                        </Typography>
                                                        <Typography variant="body1">{record.action_target || '-'}</Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        );
                                    }
                                    return null;
                                }}
                            />
                        </CardContent>
                    </Card>

                    {/* Timeline */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <AccessTime sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.messages.sections.timeline')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 2 }}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {translate('resources.messages.fields.createdAt')}
                                    </Typography>
                                    <DateField source="created_at" showTime />
                                </Box>

                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {translate('resources.messages.fields.updatedAt')}
                                    </Typography>
                                    <DateField source="updated_at" showTime />
                                </Box>

                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {translate('resources.messages.fields.createdBy')}
                                    </Typography>
                                    <TextField source="created_by" />
                                </Box>

                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {translate('resources.messages.fields.active')}
                                    </Typography>
                                    <BooleanField source="is_active" />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </SimpleShowLayout>
        </Show>
    );
};

export default MessageShow;